/* eslint-disable no-useless-escape */
import { createRegExp, RegExpHelper } from "libs/regexp-helper";
import { clamp } from "lodash-comms";

const dateSeperator = `[.\/-]`;

export function normalizeMeridiemFromInput(input: string | undefined) {
  if (!input || input[0] === "a") return "am";
  return "pm";
}

/**
 * This function receives a string that looks like a time value and attempts to normalize it
 * into a more regular looking time string. The result may be invalid.
 */
export function normalizeTimeFromInput(inputTime?: string, meridiemInput?: string) {
  if (inputTime) {
    const time = inputTime.trim().replace(" ", ":");
    const [hourPart, minutePart] = time.split(":");
    let hasMinutePart = !!minutePart;
    let hour = Number.parseInt(hourPart!.slice(0, 2), 10);
    let minute = !minutePart ? 0 : Number.parseInt(minutePart.slice(0, 2).padEnd(2, "0"), 10);

    if (hour > 23 && hour < 100) {
      const [firstNumber, secondNumber] = hour.toString();
      hour = Number.parseInt(firstNumber!, 10);
      minute = Number.parseInt(secondNumber!.padEnd(2, "0"), 10);
      hasMinutePart = true;
    }

    let meridiem: string;

    if (meridiemInput) {
      meridiem = normalizeMeridiemFromInput(meridiemInput);
    } else if (hour <= 11 && hour >= 6) {
      meridiem = "am";
    } else {
      meridiem = "pm";
    }

    if (meridiemInput || hour <= 12) {
      if (hasMinutePart) {
        return `${hour}:${minute.toString().padStart(2, "0")} ${meridiem}`;
      } else {
        return `${hour} ${meridiem}`;
      }
    }

    // The input is in a 24 hour format so we don't show the meridiem

    if (inputTime.includes(":")) return time;

    if (time.length === 4) {
      return `${time.slice(0, 2)}:${time.slice(2)}`;
    }

    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  } else {
    return meridiemInput?.[0] === "p" ? `5 pm` : `8 am`;
  }
}

/**
 * This function receives a normalized time string (i.e. the return value of normalizeTimeFromInput)
 * and parses it into hours and minutes integers.
 */
export function parseNormalizedTime(normalizedTime: string) {
  let hours = 0;
  let minutes = 0;

  // Handle formats like "8 am", "5 pm", "10:30 am", "14:45"
  const timeRegex = /(\d{1,2})(?::(\d{2}))?(?:\s*(am|pm))?/i;
  const match = normalizedTime.match(timeRegex);

  if (match) {
    hours = parseInt(match[1]!, 10);
    minutes = match[2] ? parseInt(match[2], 10) : 0;

    // Handle PM conversion
    if (match[3]?.toLowerCase() === "pm" && hours < 12) {
      hours += 12;
    }

    // Handle 12 AM special case
    if (match[3]?.toLowerCase() === "am" && hours === 12) {
      hours = 0;
    }
  }

  return { hours: clamp(hours, 0, 23), minutes: clamp(minutes, 0, 59) };
}

export function formatParsedTime(parsedTime: { hours: number; minutes: number }) {
  const meridiem = parsedTime.hours >= 12 ? "pm" : "am";
  return `${parsedTime.hours}:${parsedTime.minutes.toString().padStart(2, "0")} ${meridiem}`;
}

/**
 * This function receives a string that looks like a date value and attempts to normalize it
 * into a more regular looking date string. The result may be invalid.
 */
export function normalizeDateFromInput(month: string, day = "1", year: string | undefined) {
  const normalizedMonth = parseInt(month, 10);
  const normalizedDay = parseInt(day, 10);
  const normalizedYear = parseInt(padYearInput(year), 10);

  return `${normalizedMonth}/${normalizedDay}/${normalizedYear}`;
}

function padYearInput(year = String(new Date().getFullYear())) {
  const currentYear = String(new Date().getFullYear()).split("");

  if (year.length === 1) {
    // e.g. if today is 2025 and the user inputs "1" -> "2015"
    currentYear[2] = year;
  } else if (year.length === 2) {
    // e.g. if today is 2025 and the user inputs "26" -> "2026"
    currentYear[2] = year[0]!;
    currentYear[3] = year[1]!;
  } else if (year.length === 3) {
    // e.g. if today is 2025 and the user inputs "202" -> "2025"
    currentYear[0] = year[0]!;
    currentYear[1] = year[1]!;
    currentYear[2] = year[2]!;
  } else {
    return year;
  }

  return currentYear.join("");
}

/**
 * This function receives a string that looks like a number value and attempts to reformat this
 * number as a normalized time string. The result may be invalid.
 */
export function normalizeNumberAsTimeFromInput(value: string): string {
  let time: string;

  switch (value.length) {
    case 1:
    case 2: {
      // e.g. "5" or "12"
      time = value;
      break;
    }
    case 3: {
      // e.g. "130" -> "1:30" or "545" -> "5:45"
      time = `${value[0]}:${value.slice(1)}`;
      break;
    }
    case 4: {
      // e.g. "1030" -> "10:30" or "1245" -> "12:45"
      time = `${value.slice(0, 2)}:${value.slice(2)}`;
      break;
    }
    default: {
      // defaulting to "8" to align with Superhuman's behavior.
      time = "8";
      break;
    }
  }

  return normalizeTimeFromInput(time);
}

/**
 * Capture groups:
 * 1. Template variable
 *
 * Examples:
 * - {{9 am}}
 * - {{}}
 * - {{ }}
 * - {{ thisdafcoij}}
 */
export const templateVariable = createRegExp({
  caseInsensitive: true,
  global: true,
}).capture((b) => b.then("{{").zeroOrMore(RegExpHelper.ANYTHING_EXCEPT_NEWLINE).then("}}"));

/**
 * Capture groups:
 * 1. Time. E.g. "9", "1700", "5:30 pm", "5:"
 * 2. Meridiem?
 *
 * Examples:
 * - 9:00 am
 * - 06:00 am
 * - 12:51 p
 * - 17:00
 * - 16:00 pm
 * - 1 37
 */
export const timeWithColonOrSpaceAndMaybeMeridiem = createRegExp({
  caseInsensitive: true,
  global: true,
})
  .capture((a) =>
    a
      .optional("[012]")
      .then(RegExpHelper.NUMBER)
      .anyOf(
        (b1) => b1.then(":").optional("[0-5]"),
        (b2) => b2.then(RegExpHelper.SPACE).then("[0-5]"),
      )
      .optional(RegExpHelper.NUMBER),
  )
  .then()
  .optional(RegExpHelper.SPACE)
  .then()
  .optional((b) => b.capture((c) => c.anyOf(`am?`, `pm?`)))
  .optional(RegExpHelper.WORD_BOUNDARY);

/**
 * Capture groups:
 * 1. Time. E.g. "9", "1700", "5 30 pm"
 * 2. Meridiem
 *
 * Examples:
 * - 9 am
 * - 10p
 */
export const timeWithMeridiem = createRegExp({
  caseInsensitive: true,
  global: true,
})
  .capture((a) => a.then(RegExpHelper.NUMBER).optional(RegExpHelper.NUMBER).optional(RegExpHelper.NUMBER))
  .then()
  .optional(RegExpHelper.SPACE)
  .then()
  .capture((c) => c.anyOf(`am?`, `pm?`))
  .then(RegExpHelper.WORD_BOUNDARY);

/**
 * Capture groups
 * 1. month - mm
 * 2. day - dd
 * 3. year? - yyyy
 *
 * Examples
 * - mm/dd/yyyy
 * - m/d/yy
 * - mm/dd
 * - m/d
 * - m-d-yy
 * - m.d.yyyy
 */

// The goal of this regex is to allow incrementally typing out a date while capturing the input
// as we type. The minimum syntax for a date is "mm/" (the "/" changes our interpretation of the
// input from a number to a date). After that, "mm/dd" is allowed as well as "mm/dd/" and "mm/dd/yyyy".
// The tricky bit is that "mm/dd" and "mm/dd/" are allowed, so the second slash is optional, but
// the second slash is required when typing "mm/dd/yyyy".
export const date = createRegExp({ caseInsensitive: true, global: true })
  // mm
  .capture((a) => a.optional("[01]").then(RegExpHelper.NUMBER))
  .then(dateSeperator)
  // dd/yyyy
  .optional((a) =>
    a
      // dd
      .capture((c) => c.optional("[0123]").then(RegExpHelper.NUMBER))
      .optional((b) =>
        b.then(dateSeperator).optional((a) =>
          // yyyy
          a.capture((b) =>
            b.anyOf(
              (c1) => c1.then("[12]").then(RegExpHelper.NUMBER).then(RegExpHelper.NUMBER).optional(RegExpHelper.NUMBER),
              (c2) => c2.then(RegExpHelper.NUMBER).then(RegExpHelper.NUMBER),
              (c3) => c3.then(RegExpHelper.NUMBER),
            ),
          ),
        ),
      ),
  )
  .optional(RegExpHelper.WORD_BOUNDARY)
  .optional(RegExpHelper.SPACE);

/**
 * Capture groups:
 * 1. Number
 *
 * Examples:
 * - 1700
 * - 060
 * - 1
 */
export const number = createRegExp({
  caseInsensitive: true,
  global: true,
}).capture((a) => a.oneOrMore(RegExpHelper.NUMBER));
